import {
  useRef, useState, useEffect, useContext,
} from 'react';
import * as EmailValidator from 'email-validator';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import parse from 'html-react-parser';
import { Tooltip } from 'react-tooltip';
import SessionContext from '../../../context/SessionContext';
import styles from './LoginForm.module.scss';
import API from '../../../api/phx';
import '../../../translations/i18n';
import { getAppConfig, getLangPrefixFromBrand, getLangUrlFromBrand } from '../../../config/config';
import * as utils from '../../../utils/OrderUtils';
import Login from '../../Analytics/Login/Login';

function LoginForm({ isCCNS }) {
  const emailRef = useRef(null);
  const errRef = useRef(null);
  const orderIdRef = useRef(null);

  const [errMsg, setErrMsg] = useState('');

  const [email, setEmail] = useState('');
  const [emailIsValid, setEmailIsValid] = useState();
  const [orderId, setOrderId] = useState('');
  const [orderIdIsValid, setOrderIdIsValid] = useState();
  const [formIsValid, setFormIsValid] = useState();

  const [contactNumber, setContactNumber] = useState('');

  const [cookies, setCookie] = useCookies(['customer']);

  const [isLoading, setIsLoading] = useState(false);

  const config = getAppConfig();
  const { t } = useTranslation('common');

  const location = useLocation();

  // Check form is valid
  useEffect(() => {
    setFormIsValid(
      EmailValidator.validate(email) && orderId.trim().length >= 5,
    );
  }, [email, orderId]);

  const validateEmailHandler = () => {
    setEmailIsValid(EmailValidator.validate(email));
  };

  const validateOrderIdHandler = () => {
    setOrderIdIsValid(orderId.trim().length >= 5);
  };

  const sessionCtx = useContext(SessionContext);

  useEffect(() => {
    if (orderIdRef.current) {
      orderIdRef.current.focus();
    }
  }, []);

  useEffect(() => {
    setErrMsg('');
  }, [email, orderId]);

  const getContactNumber = async () => {
    try {
      const res = await API.get(`/content?geo=${utils.getLang()}`);
      setContactNumber(res.data.phone);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  useEffect(() => {
    if (
      location.state
      && location.state.orderStatus === 'customer_contacted_no_sale'
      && !email
      && !orderId
    ) {
      getContactNumber();
      if (utils.getLang() === 'en-us' && config.brand === 'ATD') {
        setErrMsg(
          `Oops! The quote we provided you has recently expired. Not to worry, we can easily prepare an updated one for you. Just <a href="/${config.language_prefix}/contact-us">contact us</a>.`,
        );
      } else if (config.brand === 'AA') {
        setErrMsg(
          'Oops! The quote we provided you has recently expired. Not to worry, we can easily prepare an updated one for you. Just <a href="/contactus.php">contact us</a>.',
        );
      } else if (config.brand === 'AUS') {
        setErrMsg(
          'Oops! The quote we provided you has now expired. Not to worry, we can easily prepare an updated one for you. Just contact us.',
        );
      } else {
        setErrMsg(
          'Oops! The quote we provided you has now expired. Not to worry, we can easily prepare an updated one for you. Just contact us on ',
        );
      }
    }
  }, [location.state, email, orderId, config.brand, config.language_prefix]);

  const handleLogin = async (loginEmail, loginOrderId) => {
    const loginPost = `username=${encodeURIComponent(loginEmail)}&order_id=${loginOrderId}`;
    const loginConfig = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    };
    try {
      const requestUrl = utils.appendGeoParam('/session');
      const response = await API.post(requestUrl, loginPost, loginConfig);
      const sid = response.data.id;
      const userId = response.data.user_id;
      const username = response.data.username;

      setEmail('');
      setOrderId('');

      const { onLogin } = sessionCtx;
      onLogin(userId, loginOrderId, sid);

      setCookie('user', userId, { path: '/' });
      setCookie('order', loginOrderId, { path: '/' });

      // Add logged in class to parent body.
      const parentBody = document.body;
      if (parentBody) {
        parentBody.classList.add('logged-in');
      }

      Login(
        loginEmail,
        loginOrderId,
        userId,
        sid,
        username,
        'manual through login page',
      );
    } catch (err) {
      if (!err.response) {
        setErrMsg('No Server Response');
      } else if (err.response.status === 400) {
        setErrMsg('Missing email address or OrderId');
      } else if (err.response.status === 401) {
        setErrMsg('Unauthorized');
      } else {
        setErrMsg('Please check your login details and try again');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!emailIsValid) {
      if (emailRef.current) emailRef.current.focus();
    }
    if (!orderIdIsValid) {
      if (orderIdRef.current) orderIdRef.current.focus();
    }

    if (formIsValid) {
      try {
        setIsLoading(true);
        const requestUrl = utils.appendGeoParam('/session');
        const response = await API.post(
          requestUrl,
          `username=${encodeURIComponent(email)}&order_id=${orderId}`,
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          },
        );

        // Successful login on current domain
        await handleLogin(email, orderId);
      } catch (err) {
        setIsLoading(false);
        // Handle wrong domain scenario
        if (err.response && err.response.data[0] === 'WRONG_DOMAIN') {
          const correctDomain = err.response.data.correct_domain;
          let redirectUrl = '';
          if (correctDomain === 'AA') {
            redirectUrl = config.aa_cux_url;
          } else {
            redirectUrl = `/${getLangUrlFromBrand(correctDomain)}/customer/login`;
          }
          const correctLang = getLangPrefixFromBrand(correctDomain);

          // Store login credentials in session storage for auto-login
          sessionStorage.setItem('pendingLogin', JSON.stringify({
            email,
            orderId,
            correctDomain: correctLang,
          }));

          // Redirect to the correct domain
          window.location.href = redirectUrl;
        } else if (!err.response) {
          setErrMsg('No Server Response');
        } else if (err.response.status === 400) {
          setErrMsg('Missing email address or OrderId');
        } else if (err.response.status === 401) {
          setErrMsg('Unauthorized');
        } else {
          setErrMsg('Please check your login details and try again');
        }
      }
    }
  };

  // On component mount, check for pending login
  useEffect(() => {
    const pendingLogin = sessionStorage.getItem('pendingLogin');
    if (pendingLogin) {
      try {
        setIsLoading(true);
        const { email: storedEmail, orderId: storedOrderId } = JSON.parse(pendingLogin);

        // Attempt to log in
        handleLogin(storedEmail, storedOrderId);

        // Clear the pending login
        sessionStorage.removeItem('pendingLogin');
      } catch (error) {
        setIsLoading(false);
        console.error('Error with pending login', error);
        sessionStorage.removeItem('pendingLogin');
      }
    }
  }, []);

  return (
    <section className={styles.login}>
      <h2 className={styles.customerAccountsSubtitle}>{t('login.subtitle')}</h2>
      <p className={styles.customerAccountsIntro}>{t('login.text')}</p>
      {errMsg && (
        <p ref={errRef} className={styles.errmsg} aria-live="assertive">
          {parse(t(errMsg))}
          {isCCNS
            && utils.getLang() !== 'en-us'
            && config.brand !== 'AA'
            && `${contactNumber}.`}
        </p>
      )}
      <form onSubmit={handleSubmit} data-testid="login-form">
        <fieldset className={styles.fieldset}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="orderId">
              {t('login.order_id')}
              {' '}
              <span title="Required" className={styles.required}>
                *
              </span>
              {' '}
              <span
                className={styles.tooltipTitle}
                data-tooltip-id="order-id-tooltip"
                data-tooltip-html={t('login.order_id_tooltip')}
                data-tooltip-place="bottom"
              >
                {' '}
                i
                {' '}
              </span>
              <Tooltip id="order-id-tooltip" className={styles.tooltipBody} />
            </label>
            <span className={`${styles.inputWrap} validd errorr`}>
              <span className={styles.prefix}>
                {config.brand}
                -
              </span>
              <input
                className={styles.inputNumber}
                type="number"
                id="orderId"
                ref={orderIdRef}
                onChange={(e) => setOrderId(e.target.value)}
                onBlur={validateOrderIdHandler}
                value={orderId}
                placeholder={t('login.input1')}
                required
                onWheel={(e) => e.target.blur()}
              />
            </span>
            <p className={styles.error} />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="email">
              {t('login.email')}
              {' '}
              <span title="Required" className={styles.required}>
                *
              </span>
            </label>
            <span className={`${styles.inputWrap} validd errorr`}>
              <input
                className={`${styles.inputEmail} gb-mask`}
                type="email"
                id="email"
                ref={emailRef}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={validateEmailHandler}
                value={email}
                placeholder={t('login.input2')}
                required
              />
            </span>
            <p className={styles.error} />
          </div>
          <button
            className={`${styles.button} ${isLoading && 'loading'}`}
            disabled={!formIsValid}
            type="submit"
          >
            {t('login.button_text')}
          </button>
        </fieldset>
      </form>
    </section>
  );
}

export default LoginForm;
